import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  startOfWeek, addDays, isSameMonth, isSameDay, parseISO, addMonths, subMonths,
} from "date-fns";
import { pt } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Clock, MapPin } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_COLORS = {
  pending: "bg-[#f59e0b]",
  confirmed: "bg-[#d4af37]",
  in_progress: "bg-[#3b82f6]",
  completed: "bg-[#10b981]",
  cancelled: "bg-[#ef4444]",
};

/**
 * EmployeeCalendar — month-grid calendar of the employee's assigned
 * cleanings. Clicking a day drills into the list of bookings that day.
 */
export default function EmployeeCalendar() {
  const [cursor, setCursor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [selectedDay, setSelectedDay] = useState(format(new Date(), "yyyy-MM-dd"));

  useEffect(() => {
    api.get("/bookings").then(({ data }) => setItems(data.items || []));
  }, []);

  const days = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const gridStart = startOfWeek(monthStart, { locale: pt, weekStartsOn: 1 });
    // 6 weeks covers any month layout reliably
    return eachDayOfInterval({ start: gridStart, end: addDays(gridStart, 41) });
  }, [cursor]);

  const byDate = useMemo(() => {
    const m = {};
    for (const b of items) {
      if (!m[b.scheduled_date]) m[b.scheduled_date] = [];
      m[b.scheduled_date].push(b);
    }
    return m;
  }, [items]);

  const selectedBookings = (byDate[selectedDay] || []).slice().sort(
    (a, b) => a.scheduled_time.localeCompare(b.scheduled_time)
  );

  const weekdays = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-3">Agenda</div>
        <h1 className="font-display text-5xl">O meu calendário.</h1>
        <div className="text-sm text-[#a0b2af] mt-2">
          Veja as suas limpezas atribuídas ao longo do mês. Clique num dia para ver detalhes.
        </div>
      </div>

      <div className="lux-card p-4 md:p-6" data-testid="emp-calendar">
        {/* Month navigator */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCursor(subMonths(cursor, 1))}
                  className="p-2 border border-[#1a4640] hover:border-[#d4af37]" data-testid="cal-prev">
            <ChevronLeft size={14} />
          </button>
          <div className="font-display text-2xl capitalize">
            {format(cursor, "MMMM yyyy", { locale: pt })}
          </div>
          <button onClick={() => setCursor(addMonths(cursor, 1))}
                  className="p-2 border border-[#1a4640] hover:border-[#d4af37]" data-testid="cal-next">
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekdays.map(d => (
            <div key={d} className="text-[10px] uppercase tracking-widest text-[#a0b2af] text-center">{d}</div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map(day => {
            const iso = format(day, "yyyy-MM-dd");
            const inMonth = isSameMonth(day, cursor);
            const isToday = isSameDay(day, new Date());
            const isSelected = iso === selectedDay;
            const dayBookings = byDate[iso] || [];
            return (
              <button key={iso} onClick={() => setSelectedDay(iso)}
                      className={`relative aspect-square p-1 md:p-2 text-left border transition-colors min-h-[46px] md:min-h-[64px]
                        ${isSelected ? "border-[#d4af37] bg-[#0a3a33]" : "border-[#1a4640]"}
                        ${!inMonth ? "opacity-30" : ""}
                        hover:border-[#d4af37]/60`}
                      data-testid={`cal-day-${iso}`}>
                <div className={`text-[11px] md:text-xs ${isToday ? "text-[#d4af37] font-bold" : ""}`}>
                  {format(day, "d")}
                </div>
                {dayBookings.length > 0 && (
                  <div className="absolute bottom-1 left-1 right-1 flex gap-0.5 flex-wrap">
                    {dayBookings.slice(0, 4).map(b => (
                      <span key={b.id} className={`w-1.5 h-1.5 rounded-full ${STATUS_COLORS[b.status] || "bg-[#a0b2af]"}`} />
                    ))}
                    {dayBookings.length > 4 && (
                      <span className="text-[8px] text-[#a0b2af]">+{dayBookings.length - 4}</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-4 text-[10px] text-[#a0b2af] uppercase tracking-widest">
          {Object.entries({
            confirmed: "Confirmado", in_progress: "Em curso", completed: "Concluído",
            pending: "Pendente", cancelled: "Cancelado",
          }).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[k]}`} />{v}
            </span>
          ))}
        </div>
      </div>

      {/* Selected day detail */}
      <div className="lux-card p-6" data-testid="emp-calendar-day-detail">
        <div className="eyebrow mb-4 capitalize">
          {format(parseISO(selectedDay + "T00:00:00"), "EEEE, d 'de' MMMM", { locale: pt })}
          <span className="normal-case tracking-normal text-[#a0b2af] ml-2">
            · {selectedBookings.length} limpeza{selectedBookings.length !== 1 ? "s" : ""}
          </span>
        </div>
        {selectedBookings.length === 0 && (
          <div className="text-sm text-[#a0b2af]">Sem limpezas agendadas neste dia.</div>
        )}
        <div className="space-y-3">
          {selectedBookings.map(b => (
            <Link key={b.id} to={`/funcionario/tarefa/${b.id}`}
                  className="block border border-[#1a4640] hover:border-[#d4af37]/60 p-4 rounded-sm transition-colors"
                  data-testid={`cal-booking-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs text-[#d4af37]">{b.apt_type}</span>
                    <span className="flex items-center gap-1 text-sm"><Clock size={12} /> {b.scheduled_time}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${STATUS_COLORS[b.status]} text-[#041f1b] font-bold`}>
                      {b.status}
                    </span>
                  </div>
                  {b.client_name && <div className="text-xs mt-1">{b.client_name}</div>}
                  {b.apartment_address && (
                    <div className="text-[11px] text-[#a0b2af] mt-0.5 flex items-center gap-1">
                      <MapPin size={10} className="text-[#d4af37]" /> {b.apartment_address}
                    </div>
                  )}
                </div>
                <ChevronRight size={14} className="text-[#a0b2af] flex-shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
